// displayModeHelper.tsx
import React, { useMemo } from "react"
import type { ICellRendererParams } from "ag-grid-community"
import { getContrastingTextColor } from "Shared/utils/getContrastedColor"

type DisplayMode = "displayLabel" | "displayValueAndColor" | "displayLabelAndColor"

type ScaleItem = { key: string; value: string | number }
type EnumColors = Record<string, { backgroundColor?: string }>
type ValueMapEntry = { key: string; label?: string; value?: string | number; color?: string | null }
type PersonalRule = { value: any; color: string; label?: string }
type ConfigField = { idField: string; configField?: PersonalRule[] }
type DisplayConfig = {
  uuid: string
  name?: string
  mode: DisplayMode
  scale?: ScaleItem[]
  enumColors?: EnumColors
  valueMap?: Record<string, ValueMapEntry>
}

type NormalizedConfig = DisplayConfig & {
  _byValueToKey: Map<string, string> // "3" -> "Significatif"
  _byKeyToValue: Map<string, string> // "Significatif" -> "3"
  _valueMap: Record<string, ValueMapEntry>
}

function normalizeConfig(config: DisplayConfig): NormalizedConfig {
  const scale = config.scale ?? []
  const enumColors = config.enumColors ?? {}

  const byValueToKey = new Map<string, string>()
  const byKeyToValue = new Map<string, string>()
  for (const s of scale) {
    const keyStr = String(s.key)
    const valStr = String(s.value)
    byValueToKey.set(valStr, keyStr)
    byKeyToValue.set(keyStr, valStr)
  }

  const vm: Record<string, ValueMapEntry> = {}
  if (config.valueMap && Object.keys(config.valueMap).length > 0) {
    for (const k of Object.keys(config.valueMap)) {
      const entry = config.valueMap[k] || {}
      const color =
        entry.color ??
        enumColors?.[k]?.backgroundColor ??
        enumColors?.[String(entry.value ?? "")]?.backgroundColor ??
        null
      vm[k] = { ...entry, color: color ?? null, key: entry.key ?? k }
    }
  } else {
    for (const s of scale) {
      const keyStr = String(s.key)
      const valStr = String(s.value)
      const color =
        enumColors?.[keyStr]?.backgroundColor ?? enumColors?.[valStr]?.backgroundColor ?? null
      vm[keyStr] = {
        key: keyStr,
        label: keyStr,
        value: s.value,
        color,
      }
    }
  }

  return {
    ...config,
    enumColors,
    _byValueToKey: byValueToKey,
    _byKeyToValue: byKeyToValue,
    _valueMap: vm,
  }
}

export function indexDisplayConfigs(arr: DisplayConfig[] = []) {
  const map = new Map<string, NormalizedConfig>()
  for (const raw of arr) {
    if (!raw?.uuid) continue
    map.set(String(raw.uuid), normalizeConfig(raw))
  }
  return map
}

function resolveVM(config: NormalizedConfig, raw: any): ValueMapEntry | null {
  if (raw === undefined || raw === null || raw === "") return null
  const rawStr = String(raw)

  if (config._valueMap[rawStr]) return config._valueMap[rawStr]

  const keyFromValue = config._byValueToKey.get(rawStr)
  if (keyFromValue && config._valueMap[keyFromValue]) return config._valueMap[keyFromValue]

  for (const k of Object.keys(config._valueMap)) {
    const ent = config._valueMap[k]
    if (String(ent.value ?? "") === rawStr) return ent
  }
  return null
}

function buildPersonalizationIndex(cfgField?: ConfigField | null) {
  const idx = new Map<string, { color: string; label?: string }>()
  const arr = cfgField?.configField || []
  for (const r of arr) {
    if (r?.value == null) continue
    idx.set(String(r.value), { color: r.color, label: r.label })
  }
  return idx
}

// --- React renderer (JSX) ---
export const DisplayModeCell: React.FC<
  ICellRendererParams & { config?: any; configField?: any }
> = ({ value, config, configField, colDef }) => {
  // Fallback brut si pas de config
  if (!config) return <>{String(value ?? "")}</>

  const vm = resolveVM(config, value)
  const rawStr = String(value ?? "")
  const pIndex = useMemo(() => buildPersonalizationIndex(configField), [configField])

  let text: string
  let bgColor: string | undefined | null

  if (config.mode === "displayPersonalization") {
    // Mode perso
    const rule = pIndex.get(rawStr)
    text = rule?.label ?? rawStr
    bgColor = rule?.color
  } else {
    // Comportement standard (enum)
    switch (config.mode) {
      case "displayLabel":
        text = vm?.label ?? vm?.key ?? rawStr
        break
      case "displayValueAndColor":
        text = vm?.value != null ? String(vm.value) : vm?.key ?? rawStr
        break
      case "displayLabelAndColor":
      default:
        text = vm?.label ?? vm?.key ?? rawStr
        break
    }

    if (config.mode === "displayValueAndColor" || config.mode === "displayLabelAndColor") {
      bgColor = vm?.color
    }
  }

  const style: React.CSSProperties = {
    display: "inline-block",
    padding: "0.15rem 0.5rem",
    borderRadius: 8,
    lineHeight: 1.2,
    fontSize: "0.85rem",
    ...(bgColor && { backgroundColor: bgColor, color: getContrastingTextColor(bgColor) }),
  }

  return <span style={style}>{text}</span>
}

// --- Optionnel : pour export/filtre/tri lisible ---
export function makeDisplayModeValueFormatter(config: NormalizedConfig) {
  return function (params: any) {
    const vm = resolveVM(config, params.value)
    if (!vm) return String(params.value ?? "")

    if (config.mode === "displayLabel") {
      return vm?.label ?? vm?.key ?? String(params.value ?? "")
    } else if (config.mode === "displayValueAndColor") {
      return vm?.value != null ? String(vm?.value) : vm?.key ?? String(params.value ?? "")
    } else if (config.mode === "displayLabelAndColor") {
      return vm?.label ?? vm?.key ?? String(params.value ?? "")
    } else {
      return String(params.value ?? "")
    }
  }
}
